import { createSignal, onMount } from 'solid-js';
import { isServer } from 'solid-js/web';
import {
    ThemeName,
    SectionColorTheme,
    sectionColorThemeClasses,
    textColorClasses,
    ColorName,
} from '../../style/color-classes';
import { useAppContext } from '../../app-context-provider/app-context-provider';


/**
 * Get the appropriate error text class based on section theme.
 * Uses explicit errorTextColor if defined, otherwise derives from surface:
 * - Light surfaces: primary-700 (darker red for contrast)
 * - Dark surfaces: primary-100 (lighter red for contrast)
 */
export const getErrorTextClass = (theme: SectionColorTheme | null): string => {
    if (!theme) {
        return textColorClasses[ColorName.Primary700]; // default
    }
    if (theme.errorTextColor) {
        return textColorClasses[theme.errorTextColor];
    }
    return theme.surface === 'dark'
        ? textColorClasses[ColorName.Primary100]
        : textColorClasses[ColorName.Neutral900];
};

/**
 * Hook to get the section theme from the nearest parent element with data-section-theme.
 * Falls back to page theme if no section theme is found.
 * Returns the theme and a pre-computed error text class for form validation errors.
 */
export function useSectionTheme() {
    const { pageTheme } = useAppContext();
    const [sectionTheme, setSectionTheme] = createSignal<SectionColorTheme | null>(null);
    const [errorClass, setErrorClass] = createSignal<string>(getErrorTextClass(null));

    let formRef: HTMLFormElement | null = null;

    const setRef = (el: HTMLFormElement) => {
        formRef = el;
    };

    onMount(() => {
        if (isServer || !formRef) return;

        let surface: 'dark' | 'light' | undefined;

        // Try to find section theme from DOM
        const section = formRef.closest('[data-section-theme]');
        if (section) {
            const themeAttr = section.getAttribute('data-section-theme');
            if (themeAttr && themeAttr in sectionColorThemeClasses) {
                const theme = sectionColorThemeClasses[themeAttr as ThemeName];
                setSectionTheme(theme);
                setErrorClass(getErrorTextClass(theme));
                surface = theme.surface;
            }
        }

        // Fall back to page theme surface
        if (!surface) {
            surface = pageTheme()?.colors?.surface;
            if (surface) {
                setErrorClass(getErrorTextClass({
                    classes: '',
                    bgColor: ColorName.noValue,
                    textColor: ColorName.noValue,
                    surface
                }));
            }
        }

        // Set data attribute on form for CSS-based focus ring styling
        formRef.dataset.surface = surface ?? 'dark';
    });

    // Return sectionTheme with reactive fallback to pageTheme when no section theme detected
    const effectiveSectionTheme = () => sectionTheme() ?? pageTheme()?.colors ?? null;

    return {
        setRef,
        sectionTheme: effectiveSectionTheme,
        errorClass,
    };
}
